'use client';

import {
  type AddressFormInputValidation,
  AddressFormV3,
  type AddressFormV3Props,
  FieldName,
  mergeValidationConfigs,
} from '@jouzen/ecom-components/v3/AddressForm';
import { type FormControlChangeEvent } from '@jouzen/ecom-components/v3/FormControl';
import { cx } from 'class-variance-authority';
import merge from 'lodash/merge';
import noop from 'lodash/noop';
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import { type CountryData } from '@/app/hooks/useCountryData';
import useFocusFirstErrorComponent from '@/app/hooks/useFocusFirstErrorComponent';
import { logger as browserLogger } from '@/logger/logger.browser';
import { type AddressFormValidation } from '@/queries/types/graphql';

import { transformValidationData } from './utils';

export interface AddressFormProps extends Omit<
  AddressFormV3Props,
  'className'
> {
  readonly addressFormValidation: AddressFormValidation;
  readonly className?: string; // used on this wrapper element
  readonly countryData: CountryData;
  readonly countryTooltip?: ReactNode; // rendered next to the Country field label
  readonly formClassName?: AddressFormV3Props['className']; // passed down to form
  readonly loading?: boolean;
  readonly setSelectedCountryCode: Dispatch<SetStateAction<string>>;
}

const AddressForm = ({
  addressFormValidation,
  className = '',
  countryData,
  countryTooltip,
  excludedFields: initExcludedFields = [],
  fieldProps: fieldPropsOverrides = {},
  formClassName = '',
  hiddenFields = [],
  initialValues = {},
  inputValidation: inputValidationOverrides,
  loading: isLoading = false,
  selectedCountryCode,
  setSelectedCountryCode,
  submitButton,
  onFormSubmit = noop,
  onValidationError = noop,
  ...formProps
}: AddressFormProps): JSX.Element => {
  const formUuid = useId();
  const formId = formProps.id ?? `address-form-${formUuid}`;

  // focus on first error, on validation
  const formRef = useRef<HTMLDivElement>(null);
  const checkoutFocusFirstErrorComponent = useFocusFirstErrorComponent(formRef);

  // Portal target: the country label element rendered by AddressFormV3
  const [countryLabelEl, setCountryLabelEl] = useState<HTMLElement | null>(
    null,
  );
  useEffect(() => {
    if (!countryTooltip) return;
    const countrySelect = formRef.current?.querySelector<HTMLSelectElement>(
      'select[name="country"]',
    );
    const label =
      countrySelect && formRef.current
        ? formRef.current.querySelector<HTMLElement>(
            `label[for="${countrySelect.id}"]`,
          )
        : null;
    if (label) {
      // Make the label a flex container so the tooltip sits inline next to the
      // label text (the inner Typography span has display:block which would
      // otherwise push the portal content onto a new line).
      label.style.display = 'flex';
      label.style.alignItems = 'center';
      label.style.gap = '0.25rem';
    }
    setCountryLabelEl(label);
  }, [countryTooltip, isLoading]);

  const excludedFields = useMemo(
    () =>
      countryData.hasRegions
        ? initExcludedFields
        : [...initExcludedFields, FieldName.State],
    [countryData.hasRegions, initExcludedFields],
  );

  const fieldProps = useMemo(
    () =>
      merge({}, fieldPropsOverrides, {
        [FieldName.State]: {
          options: countryData.regionOptions,
        },
        [FieldName.Country]: {
          onChange: (evt: FormControlChangeEvent) => {
            fieldPropsOverrides[FieldName.Country]?.onChange?.(evt);
            setSelectedCountryCode(evt.target.value);
          },
          options: countryData.countryOptions,
        },
        [FieldName.Attention]: {
          clickToShow: false,
        },
      }),
    [
      countryData.countryOptions,
      countryData.regionOptions,
      fieldPropsOverrides,
      setSelectedCountryCode,
    ],
  );

  const inputValidation = useMemo(() => {
    const countryValidation: AddressFormInputValidation =
      transformValidationData(addressFormValidation);

    // merge country data from server with overrides, using our custom fn
    const config: AddressFormInputValidation = mergeValidationConfigs(
      countryValidation,
      inputValidationOverrides as unknown as Partial<AddressFormInputValidation>,
    );

    return config;
  }, [addressFormValidation, inputValidationOverrides]);

  const handleValidationError: AddressFormV3Props['onValidationError'] = (
    errors,
    values,
  ) => {
    // Fire callback
    onValidationError(errors, values);

    // Focus on first invalid field
    checkoutFocusFirstErrorComponent();
  };

  const handleInvalidFields = (invalidFields: FieldName[]) => {
    browserLogger.warn('All hidden fields must provide valid initialValues', {
      invalidFields,
    });
  };

  return (
    <div ref={formRef} className={cx(className)}>
      {countryTooltip &&
        countryLabelEl &&
        createPortal(countryTooltip, countryLabelEl)}
      <AddressFormV3
        key={formId} // updating key forces re-render of form, forcing an update of initialValues
        id={formId}
        noValidate
        {...formProps}
        className={formClassName}
        excludedFields={excludedFields}
        fieldProps={fieldProps}
        hiddenFields={hiddenFields}
        initialValues={{
          ...initialValues,
          country: selectedCountryCode,
        }}
        optionalFields={[FieldName.Attention]}
        inputValidation={inputValidation}
        loading={isLoading}
        onFormSubmit={onFormSubmit}
        onInvalidFields={handleInvalidFields}
        onValidationError={handleValidationError}
        selectedCountryCode={selectedCountryCode}
        submitButton={submitButton}
      />
    </div>
  );
};

export default AddressForm;
